import type { DraggableSyntheticListeners } from "@dnd-kit/core";
import { Archive, Check, Close, DragIndicator, Edit } from "@mui/icons-material";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { uiVisibleErrorKey } from "../../../utils/errors";
import type { CallInReason } from "../../../utils/models";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { useArchiveCallInReason, useUpdateCallInReason } from "./callInReasonsHooks";

const CALL_IN_REASON_MAX_LABEL_LENGTH = 100;

export const isCallInReasonLabelValid = (value: string): boolean => {
	const trimmed = value.trim();
	return trimmed.length > 0 && trimmed.length <= CALL_IN_REASON_MAX_LABEL_LENGTH;
};

export const CallInReasonRow: FC<{
	reason: CallInReason;
	departmentId: string;
	handleListeners: DraggableSyntheticListeners;
}> = ({ reason, departmentId, handleListeners }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const updateMutation = useUpdateCallInReason();
	const archiveMutation = useArchiveCallInReason();
	const [draftLabel, setDraftLabel] = useState<string | null>(null);
	const [confirmingArchive, setConfirmingArchive] = useState(false);

	const handleStartEdit = () => setDraftLabel(reason.label);
	const handleCancelEdit = () => setDraftLabel(null);

	const handleSave = async () => {
		if (draftLabel === null) return;
		const trimmed = draftLabel.trim();
		if (!trimmed) {
			showSnackbar(t("call.in.reason.label.empty"), "error");
			return;
		}
		try {
			track("call_in_reason_update_start", { department_id: departmentId, reason_id: reason.id });
			await updateMutation.mutateAsync({ reasonId: reason.id, label: trimmed });
			track("call_in_reason_update_success", { department_id: departmentId, reason_id: reason.id });
			setDraftLabel(null);
		} catch (e) {
			const message = e instanceof Error ? e.message : undefined;
			trackError("call_in_reason_update_error", { department_id: departmentId, reason_id: reason.id, error_message: message });
			// The backend says why a rename was refused (a duplicate name, an archived reason), so show its reason.
			// DEFAULT: any other failure — network, or a refusal without a reason — gets the generic message.
			showSnackbar(t(uiVisibleErrorKey(e) ?? "call.in.reason.save.failed"), "error");
		}
	};

	const handleConfirmArchive = async () => {
		try {
			track("call_in_reason_archive_start", { department_id: departmentId, reason_id: reason.id });
			await archiveMutation.mutateAsync(reason.id);
			track("call_in_reason_archive_success", { department_id: departmentId, reason_id: reason.id });
			setConfirmingArchive(false);
		} catch (e) {
			const message = e instanceof Error ? e.message : undefined;
			trackError("call_in_reason_archive_error", { department_id: departmentId, reason_id: reason.id, error_message: message });
			showSnackbar(t("call.in.reason.archive.failed"), "error");
		}
	};

	const isEditing = draftLabel !== null;
	const isBusy = updateMutation.isPending || archiveMutation.isPending;

	return (
		<Box sx={{ mb: 1 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					p: 1,
					borderRadius: 1,
					bgcolor: "#F3F1F8",
				}}
				data-testid={`call-in-reason-row-${reason.id}`}
			>
				<Box
					{...handleListeners}
					sx={{ cursor: "grab", display: "flex", alignItems: "center", color: "#999", touchAction: "none", "&:active": { cursor: "grabbing" } }}
					data-testid={`call-in-reason-drag-${reason.id}`}
				>
					<DragIndicator fontSize="small" />
				</Box>

				{isEditing ? (
					<SystemValidationTextField
						value={draftLabel}
						onChange={(e) => setDraftLabel(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") void handleSave();
							if (e.key === "Escape") handleCancelEdit();
						}}
						validation={{
							validationCallback: isCallInReasonLabelValid,
							helperText: {
								invalid: draftLabel.trim().length === 0 ? t("call.in.reason.label.empty") : t("call.in.reason.label.too.long"),
							},
							performValidation: "onlyAfterInitialChange",
							showValidWhenValid: "never",
						}}
						reserveHeightForHelperText={false}
						formControlSx={{ flex: 1 }}
						injectedProps={{ slotProps: { htmlInput: { "data-testid": `call-in-reason-edit-label-${reason.id}` } } }}
					/>
				) : (
					<Typography sx={{ flex: 1, color: "#4D5579", fontWeight: 500, fontSize: 14 }}>{reason.label}</Typography>
				)}

				{isEditing ? (
					<Stack direction="row" spacing={1}>
						<Tooltip title={t("save")}>
							<span>
								<IconButton size="small" onClick={handleSave} disabled={isBusy} data-testid={`call-in-reason-save-${reason.id}`}>
									<Check fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title={t("cancel")}>
							<span>
								<IconButton size="small" onClick={handleCancelEdit} disabled={isBusy} data-testid={`call-in-reason-cancel-${reason.id}`}>
									<Close fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Stack>
				) : (
					<Stack direction="row" spacing={1}>
						<Tooltip title={t("edit")}>
							<span>
								<IconButton size="small" onClick={handleStartEdit} disabled={isBusy} data-testid={`call-in-reason-edit-${reason.id}`}>
									<Edit fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title={t("call.in.reason.archive")}>
							<span>
								<IconButton size="small" onClick={() => setConfirmingArchive(true)} disabled={isBusy} data-testid={`call-in-reason-archive-${reason.id}`}>
									<Archive fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					</Stack>
				)}
			</Box>
			<SystemDialog
				open={confirmingArchive}
				onClose={() => setConfirmingArchive(false)}
				title={t("call.in.reason.archive.confirm.title")}
				body={t("call.in.reason.archive.confirm.body", { label: reason.label })}
				confirm={t("call.in.reason.archive")}
				onConfirm={handleConfirmArchive}
				confirmLoading={archiveMutation.isPending}
				dataTestId={`call-in-reason-archive-confirm-${reason.id}`}
			/>
		</Box>
	);
};
